import React, {useEffect, useState} from 'react';
import {connect} from "react-redux";
import PropTypes from "prop-types";
import ReactFlow, {
  addEdge,
  MiniMap,
  Controls,
  Background,
  applyEdgeChanges,
  applyNodeChanges,
  updateEdge,
} from 'react-flow-renderer';

import WizardNode from "./WizardNode";
import {updateWizardList} from "../wizardActions";
import WizardNodeStart from "./WizardNodeStart";
import {WIZARD_GROUP_TYPES} from "../WizardItem/wizardItemInterface";
import {AVAILABLE_VISUALIZERS} from "../../../constants/dashboard";

const multiSelectionKeyCode = navigator.userAgent.indexOf('Mac OS X') !== -1 ? "Meta" : "Control";
const nodeTypes = {wizardNode: WizardNode };
let updateTimeout = null;
const cloneNodes = (nodes) => nodes ? nodes.map(node => Object.assign({}, node)) : [];


const WizardGroupsFlowCanvas = (props) => {

  const {wizardActiveItem, wizardEdges, updateWizardList, wizardNodesCount, setFlowInstance, featureGroups,
    setAvailableFilterConnections, wizardVisualizer, workSheetWizardSelectionsActive, workSheetWizardSelectionsClick,
    workSheetWizardSelectionsGroupSelected,} = props;
  const [nodes, setNodes] = useState(wizardActiveItem ? cloneNodes(wizardActiveItem.wizardNodes) : []);
  const [keyDown, setKeyDown] = useState("");

  const onNodesChange = (changes) => {
    const newNodes = applyNodeChanges(changes, nodes).map(node => Object.assign({}, node, {data: null}));
    setNodes(newNodes);
    clearTimeout(updateTimeout);
    updateTimeout = window.setTimeout(() => {
      wizardActiveItem.wizardNodes = newNodes.map(node => Object.assign({}, node, {data: null}))
      // console.log("NEW wizardActiveItem.wizardGroups", wizardActiveItem.wizardGroups);
      updateWizardList(wizardActiveItem);
    }, 500)
  };

  const onEdgesChange = (changes) => { // on delete edge
    if (keyDown === multiSelectionKeyCode) {
      if (Array.isArray(changes) && changes.length === 1) {
        const alreadySelected = wizardActiveItem.wizardEdges.find(edge => changes[0].id === edge.id);
        if (alreadySelected && alreadySelected.selected) {
          changes[0].selected = !alreadySelected.selected;
        }
      } else {
        return // cases when we are holding Control and unselecting wizard group
      }
    }
    wizardActiveItem.wizardEdges = applyEdgeChanges(changes, wizardEdges);
    wizardActiveItem.wizardGroups = setAvailableFilterConnections(wizardActiveItem.wizardEdges);
    return updateWizardList(wizardActiveItem);
  };

  const onEdgeUpdate = (oldEdge, newConnection) => { // on change edge
    wizardActiveItem.wizardEdges = updateEdge(oldEdge, newConnection, wizardEdges);
    wizardActiveItem.wizardGroups = setAvailableFilterConnections(wizardActiveItem.wizardEdges);
    return updateWizardList(wizardActiveItem);
  };

  // console.log("wizardEdges", wizardEdges);

  const onConnect = (params) => { // on add new edge
    wizardActiveItem.wizardEdges = addEdge(params, wizardEdges);
    wizardActiveItem.wizardGroups = setAvailableFilterConnections(wizardActiveItem.wizardEdges);
    return updateWizardList(wizardActiveItem);
  };

  const onInit = (reactFlowInstance) => {
    console.log('flow loaded:', reactFlowInstance)
    reactFlowInstance.setCenter(400, 200);
    reactFlowInstance.zoomTo(1);
    if (setFlowInstance) {
      setFlowInstance(reactFlowInstance)
    }
  }

  useEffect(() => {
    setNodes(cloneNodes(wizardActiveItem.wizardNodes))
  }, [wizardNodesCount]);

  useEffect(() => {
    const setStateKeyDown = (event) => {
      // console.log("", event.key);
      setKeyDown(event.key);
    }
    const setStateKeyUp = () => {
      setKeyDown("");
    }
    document.addEventListener("keydown", setStateKeyDown);
    document.addEventListener("keyup", setStateKeyUp);
    return () => {
      document.removeEventListener("keydown", setStateKeyDown);
      document.removeEventListener("keyup", setStateKeyUp);
    }
  }, []);

  const isHugeWizard = wizardActiveItem.wizardGroups.length >= 500;

  const renderNodes = nodes ? nodes.map(wizardNode => {
    if (wizardNode.type === "input") {
      wizardNode.data = {
        label: (
          <WizardNodeStart wizardActiveItem={wizardActiveItem}/>
        ),
      }
    } else {
      const wizardGroup = wizardActiveItem.wizardGroups.find(wizGroup => wizGroup.id === wizardNode.id);
      const noGroupAvailable = wizardGroup && wizardGroup.nodeTypes === WIZARD_GROUP_TYPES.group
        && !featureGroups.some(featureGroup => featureGroup.groupName === wizardGroup.groupName);
      const leadStep = (wizardVisualizer === AVAILABLE_VISUALIZERS.leadform) && wizardGroup && wizardGroup.leadStep
        ? wizardGroup.leadStep
        : "";
      wizardNode.data = {
        wizardGroup,
        wizardActiveItem,
        noGroupAvailable,
        leadStep,
        isHugeWizard,
        workSheetWizardSelectionsActive,
        workSheetWizardSelectionsGroupSelected,
        workSheetWizardSelectionsClick,
      };
      wizardNode.style = wizardNode.style || {
        background: 'none',
        padding: 0,
        border: 'none',
        width: 300,
        height: 140
      };
    }
    return wizardNode
  }).filter(wizardNode => (wizardNode.type === "input") || (wizardNode.data && wizardNode.data.wizardGroup)) : [];

  // console.log("renderNodes", renderNodes);

  return (
    <ReactFlow
      minZoom={0.1}
      nodeTypes={nodeTypes}
      nodes={renderNodes}
      edges={wizardEdges}
      multiSelectionKeyCode={multiSelectionKeyCode}
      onNodesChange={onNodesChange}
      onEdgesChange={onEdgesChange}
      onEdgeUpdate={onEdgeUpdate}
      onConnect={onConnect}
      onInit={onInit}
      fitView
      attributionPosition="bottom-right"
    >
      <MiniMap
        nodeStrokeColor={(n) => {
          if (n.style && n.style.background) return n.style.background;
          if (n.type === 'input') return '#0041d0';
          if (n.type === 'output') return '#ff0072';
          if (n.type === 'default') return '#1a192b';

          return '#eee';
        }}
        nodeColor={(n) => {
          if (n.style && n.style.background) return n.style.background;

          return '#fff';
        }}
        nodeBorderRadius={2}
      />
      <Controls />
      <Background color="#2E2E2E" variant="lines" gap={30} size={1}  />
    </ReactFlow>
  );
};

WizardGroupsFlowCanvas.propTypes = {
  wizardActiveItem: PropTypes.object.isRequired,
  setFlowInstance: PropTypes.func,
  setAvailableFilterConnections: PropTypes.func,
}

const mapStateToProps = (store, ownProps) => {
  const {wizardEdges} = ownProps.wizardActiveItem ? ownProps.wizardActiveItem : {wizardEdges:[]}
  const {wizardNodesCount, wizardVisualizer} = store.wizardState;
  const {featuresGroups} = store.projectScopeNew;
  const {workSheetWizardSelectionsActive, workSheetWizardSelectionsGroupSelected, workSheetWizardSelectionsClick} = store.discountsState;

  return {
    wizardEdges: wizardEdges.map(edge => Object.assign({}, edge)),
    wizardNodesCount,
    featureGroups: featuresGroups,
    wizardVisualizer,
    workSheetWizardSelectionsActive,
    workSheetWizardSelectionsGroupSelected,
    workSheetWizardSelectionsClick,
  };
};

const mapDispatchToProps = {
  updateWizardList,
};

export default connect(mapStateToProps, mapDispatchToProps)(WizardGroupsFlowCanvas)
